import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Ban, Copy, MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { DataTable, type DataTableFeatures } from "~/components/data-table";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";
import type { ProductRow } from "~/features/admin/products/types";

interface ProductsTableProps {
  rows: ProductRow[];
  onSelect: (id: string) => void;
  emptyMessage: string;
  globalFilter: string;
  onGlobalFilterChange: (next: string) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
}

const helper = createColumnHelper<DataTableFeatures, ProductRow>();

export function ProductsTable({
  rows,
  onSelect,
  emptyMessage,
  globalFilter,
  onGlobalFilterChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
}: ProductsTableProps) {
  const { t } = useTranslation();

  const handleCopyId = async (id: string) => {
    try {
      await copyToClipboard(id);
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };

  const columns: ColumnDef<DataTableFeatures, ProductRow, any>[] = [
    helper.display({
      id: "deactivatedAt",
      header: () => "",
      enableSorting: false,
      cell: (info) => {
        if (!info.row.original.isDeactivated) return null;
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <Ban className="h-4 w-4 text-warning" aria-label={t("ui.deactivated")} />
            </TooltipTrigger>
            <TooltipContent>{t("ui.deactivated")}</TooltipContent>
          </Tooltip>
        );
      },
    }),
    helper.accessor("name", {
      id: "name",
      header: () => t("model.common.name"),
      cell: (info) => <span className="font-medium text-fg-1">{info.getValue()}</span>,
    }),
    helper.accessor("originHosts", {
      id: "originHosts",
      header: () => t("model.product.originHosts"),
      cell: (info) => <span className="text-fg-2">{info.getValue()}</span>,
    }),
    helper.display({
      id: "actions",
      header: () => "",
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center justify-end gap-1.5">
            <Button type="button" variant="outline" size="sm" onClick={() => onSelect(row.id)}>
              {t("ui.view")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="icon" aria-label={t("ui.operations")}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => handleCopyId(row.id)}>
                  <Copy />
                  <span>{t("ui.copyId")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    }),
  ];

  return (
    <DataTable<ProductRow>
      columns={columns}
      data={rows}
      emptyMessage={emptyMessage}
      globalFilter={globalFilter}
      onGlobalFilterChange={onGlobalFilterChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
    />
  );
}
